import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { flushSync } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { ArrowDown, ArrowRight, ArrowUpRight, Camera, MapPinned, MonitorPlay, Radio, Smartphone, Users } from 'lucide-react'
import { PlayHeadline } from '@/components/brand/PlayHeadline'
import { DiscordSupportLink } from '@/components/DiscordSupportLink'
import { SiteHeader } from '@/components/SiteHeader'
import { SiteFooter } from '@/components/SiteFooter'
import { Button, ButtonLink } from '@/components/play'
import { useLiveHeroEnabled } from '@/components/landing/live-hero/useLiveHeroEnabled'
import { hasResumableDemo } from '@/components/landing/live-hero/demoSession'
import { LiveHeroBoundary } from '@/components/landing/live-hero/LiveHeroBoundary'
import './LandingPage.css'

const createEventUrl = '/signup?redirect=%2Fapp%3Fcreate%3D1'
// The live hero (map, LiveKit viewer) is its own chunk, imported only when the flag is on.
const loadLiveHero = () => import('@/components/landing/live-hero/LiveHero')
const LiveHero = lazy(loadLiveHero)

type ViewTransitionDocument = Document & { startViewTransition?: (update: () => void) => unknown }

/** Photo ↔ map: a View Transition where supported and motion is welcome, otherwise a plain CSS fade. */
function swapHero(update: () => void): boolean {
  const doc = document as ViewTransitionDocument
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  if (typeof doc.startViewTransition === 'function' && !reduced) {
    doc.startViewTransition(() => flushSync(update))
    return true
  }
  update()
  return false
}

const steps = [
  { number: '01', icon: Camera, title: 'Bring your cameras.', text: 'Invite cameras with a link or QR code.' },
  { number: '02', icon: MapPinned, title: 'See the whole picture.', text: 'Find a camera on the map. Tap to watch.' },
  { number: '03', icon: MonitorPlay, title: 'Make it your show.', text: 'Mix in OBS. Bring your show back to the event.' },
]

/**
 * The header sits over the hero's photograph but outside main, so it is the page's banner landmark (a header inside
 * main is none). The hero leaves room for it at the top: its height, measured as it changes (the account, the
 * breakpoints), is `--landing-header-height` on the page.
 */
function useHeaderRoom(page: RefObject<HTMLDivElement>) {
  useLayoutEffect(() => {
    const root = page.current
    const header = root?.querySelector<HTMLElement>(':scope > .play-site-header')
    if (!root || !header) return
    const measure = () => root.style.setProperty('--landing-header-height', `${header.offsetHeight}px`)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(header)
    return () => observer.disconnect()
  }, [page])
}

export function LandingPage() {
  const location = useLocation()
  const page = useRef<HTMLDivElement>(null)
  useHeaderRoom(page)
  const liveHeroEnabled = useLiveHeroEnabled()
  const [heroMode, setHeroMode] = useState<'photo' | 'live'>('photo')
  const [liveUnavailable, setLiveUnavailable] = useState(false)
  const [liveFade, setLiveFade] = useState(false)
  const goLiveRef = useRef<HTMLButtonElement>(null)
  const createEventRef = useRef<HTMLAnchorElement>(null)
  const returnFocus = useRef<'go-live' | 'create-event' | null>(null)
  const resumeChecked = useRef(false)

  const openLive = useCallback((transition = true) => {
    loadLiveHero()
      .then(() => {
        if (!transition) return setHeroMode('live')
        setLiveFade(!swapHero(() => setHeroMode('live')))
      })
      .catch(() => setLiveUnavailable(true))
  }, [])
  const closeLive = useCallback(() => {
    returnFocus.current = 'go-live'
    swapHero(() => setHeroMode('photo'))
  }, [])
  const liveUnavailableNow = useCallback(() => {
    returnFocus.current = 'create-event'
    setLiveUnavailable(true)
    swapHero(() => setHeroMode('photo'))
  }, [])

  useEffect(() => {
    if (heroMode !== 'photo' || !returnFocus.current) return
    const target = returnFocus.current === 'go-live' ? goLiveRef.current ?? createEventRef.current : createEventRef.current
    returnFocus.current = null
    target?.focus({ preventScroll: true })
  }, [heroMode, liveUnavailable])

  // Flag on: warm the chunk when the browser is idle, and reopen a demo this tab already started.
  useEffect(() => {
    if (!liveHeroEnabled) return
    if (!resumeChecked.current) {
      resumeChecked.current = true
      if (hasResumableDemo()) {
        openLive(false)
        return
      }
    }
    const idle = window.requestIdleCallback?.(() => void loadLiveHero().catch(() => undefined))
    return () => {
      if (idle !== undefined) window.cancelIdleCallback?.(idle)
    }
  }, [liveHeroEnabled, openLive])

  useEffect(() => {
    if (!location.hash) return
    const id = location.hash.slice(1)
    document.getElementById(id)?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
  }, [location.hash])

  return (
    <div ref={page} className="play-theme play-landing play-site">
      <a className="play-landing__skip" href="#main">Skip to content</a>
      <SiteHeader tone="inverse" />
      <main id="main">
      <section className={`play-landing__hero${liveHeroEnabled && heroMode === 'live' ? ' play-landing__hero--live' : ''}`} aria-labelledby="hero-title">
        <img className="play-landing__hero-image" src="/brand/play/alpine-ridge.webp" alt="An imagined alpine ridge with trail runners making their way towards the summit" width="1536" height="1024" fetchPriority="high" />
        <div className="play-landing__hero-shade" aria-hidden="true" />
        {liveHeroEnabled && heroMode === 'live' && (
          <div className={`play-landing__live${liveFade ? ' play-landing__live--fade' : ''}`}>
            <LiveHeroBoundary onError={liveUnavailableNow}>
              <Suspense fallback={null}>
                <LiveHero onClose={closeLive} onUnavailable={liveUnavailableNow} />
              </Suspense>
            </LiveHeroBoundary>
          </div>
        )}
        <div className="play-landing__hero-body" hidden={(liveHeroEnabled && heroMode === 'live') || undefined}>
          <div className="play-landing__hero-copy">
            <p className="play-landing__eyebrow">Endurance. Experienced together.</p>
            <h1 id="hero-title"><PlayHeadline words={['SWEAT.', 'STREAM.', 'HOST.', 'WATCH.']} /></h1>
            <p className="play-landing__hero-description">For the ones out there giving everything.{' '}<br />And everyone who wants to be right there with them.</p>
            <div className="play-landing__hero-actions">
              <ButtonLink to={createEventUrl} ref={createEventRef}>Create event <ArrowUpRight aria-hidden="true" /></ButtonLink>
              {liveHeroEnabled && !liveUnavailable && (
                <Button variant="inverse" ref={goLiveRef} onClick={() => openLive()}>
                  <Smartphone aria-hidden="true" /> Try it live
                </Button>
              )}
              <a className="play-landing__text-link" href="#experience">See how it works <ArrowDown size={16} aria-hidden="true" /></a>
            </div>
            <div className="play-landing__support">
              <p>Need a hand? Get support from our crew.</p>
              <DiscordSupportLink variant="inverse" />
            </div>
          </div>
          <p className="play-landing__scene-note"><span>Out there. Together.</span>Every climb.<br />Every comeback.</p>
        </div>
      </section>

      {/* A list: a name on a plain div is ignored. */}
      <div className="play-landing__disciplines" role="list" aria-label="For every kind of endurance">
        {['Trail & ultra', 'Gravel & MTB', 'Road running', 'Triathlon'].map((sport) => <span key={sport} role="listitem">{sport}<span aria-hidden="true">✳</span></span>)}
      </div>

      <section className="play-landing__section play-landing__experience" id="experience" aria-labelledby="experience-title">
        <div className="play-landing__section-heading">
          <div><p className="play-landing__eyebrow">01 / A front row for everyone</p><h2 id="experience-title">ONE EVENT.<br /><span className="play-landing__highlight">MANY EXPERIENCES.</span></h2></div>
          <p>The front runners. The back markers. Your person.<br />Bring more of the race into view.</p>
        </div>
        <div className="play-landing__steps">
          {steps.map(({ number, icon: Icon, title, text }) => (
            <article key={number}>
              <div className="play-landing__number" aria-hidden="true"><span>{number}</span></div>
              <h3>{title}</h3>
              <p>{text}</p>
              <Icon size={21} strokeWidth={1.75} aria-hidden="true" />
            </article>
          ))}
        </div>
      </section>

      <section className="play-landing__section play-landing__mission" aria-labelledby="mission-title">
        <figure className="play-landing__mission-photo">
          <img src="/brand/play/cape-dawn.webp" alt="An imagined group of mountain bikers riding through the Cape mountains at first light" width="1536" height="1024" loading="lazy" />
          <figcaption>The long way is the point.</figcaption>
        </figure>
        <div className="play-landing__mission-copy">
          <p className="play-landing__eyebrow">02 / The best views are human</p>
          <h2 id="mission-title">GREAT RACES.<br />REAL PEOPLE.<br /><span className="play-landing__highlight">ALL TOGETHER.</span></h2>
          <p>Cameras, the map, and your live show, together.<br />Dot watching just became a lot more fun.</p>
          <div className="play-landing__community">
            <span><Radio aria-hidden="true" /> Your event</span><span><Users aria-hidden="true" /> Your people</span>
          </div>
        </div>
      </section>

      <section className="play-landing__hosts" id="hosts" aria-labelledby="hosts-title">
        <div className="play-landing__section">
          <div><p className="play-landing__eyebrow">For race organizers, clubs & community voices</p><h2 id="hosts-title">YOU KNOW THE SPORT.<br />BRING IT TO LIFE.</h2></div>
          <div><p>An event. A few cameras. Your people.<br />Start with a name. We’ll take it from there.</p><ButtonLink to={createEventUrl} variant="inverse">Create event <ArrowRight aria-hidden="true" /></ButtonLink></div>
        </div>
      </section>

      </main>

      <SiteFooter />
    </div>
  )
}
